import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { ShiftManagementRolesRequirementsFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useDragShift } from 'modules/shift_management/hooks/useDragAndDrop'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { Shift } from 'modules/shift_management/lib/types'
import type UnassignedShift from 'modules/shift_management/store/internal/models/UnassignedShift'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

import AssignEmployeesModal from '../AssignEmployeesModal'
import EditUnassignedShiftModal from '../EditUnassignedShiftModal'
import SeeUnassignedShiftModal from '../SeeUnassignedShiftModal'
import UnassignedShiftMenu from '../UnassignedShiftMenu'
import { RolesDetails } from './RolesDetails'
import ShiftDetails from './ShiftDetails'

interface Props {
  unassignedShift: UnassignedShift
  shiftsByDate: Shift[]
  plannedBreaks: PlannedBreak[]
  contractVersions: ContractVersions
  roles: ShiftManagementRolesRequirementsFragment[]
}

const UnassignedShiftItem = ({
  unassignedShift,
  shiftsByDate,
  plannedBreaks,
  contractVersions,
  roles,
}: Props) => {
  const [{ view }] = useSchedule()
  const [openEditModal, setOpenEditModal] = React.useState<boolean>(false)
  const [openSeeModal, setOpenSeeModal] = React.useState<boolean>(false)
  const [openAssignEmployeesModal, setOpenAssignEmployeesModal] = React.useState<boolean>(false)

  const color = unassignedShift.color ? unassignedShift.color : 'grey400'

  const originalCounter = unassignedShift.counter
  const assignedShifts = unassignedShift.assignedShifts(shiftsByDate)
  const totalAssignedShifts = assignedShifts.length
  const currentCounter = originalCounter - totalAssignedShifts
  const disabled = currentCounter === 0
  const canDragAndDrop = !disabled

  const { isDragging, drag } = useDragShift({
    unassignedShift,
    options: { dropEffect: 'move' },
  })

  const onSeeShiftAction = () => {
    track(trackingEvents.shiftManagement.goToDetailsUnassignedShiftStart, {})
    setOpenSeeModal(true)
  }

  const onCloseSeeShiftModal = () => {
    setOpenSeeModal(false)
  }

  const onOpenEditModal = () => {
    track(trackingEvents.shiftManagement.editUnassignedShiftStart, {
      from: 'show_details_modal',
    })
    // This is needed because this action is inside the SeeShiftModal
    setOpenEditModal(true)
  }

  const onCloseEditShiftModal = () => {
    setOpenEditModal(false)
  }

  const onEditAction = () => {
    track(trackingEvents.shiftManagement.editUnassignedShiftStart, {
      from: 'contextual_button',
    })
    setOpenEditModal(true)
  }

  const onAssignEmployeesAction = () => {
    track(trackingEvents.shiftManagement.assignEmployeesUnassignedShiftStart, {})
    setOpenAssignEmployeesModal(true)
  }

  const onCloseAssignShiftModal = () => {
    setOpenAssignEmployeesModal(false)
  }

  return (
    <>
      <Box
        minHeight={view.cellMinHeight}
        flexDirection="column"
        justifyContent="center"
        cursor={canDragAndDrop ? 'pointer' : 'default'}
        width="full"
        paddingY="s4"
        paddingX="s4"
        borderRadius={{ all: 'abs006' }}
        border={{
          all: {
            width: 's2',
            style: 'dashed',
            color: color,
          },
        }}
        style={{
          opacity: (canDragAndDrop && isDragging) || disabled ? '0.5' : '1.0',
          transform: canDragAndDrop ? 'translate(0, 0)' : undefined,
        }}
        ref={canDragAndDrop ? drag : undefined}
      >
        <Box flexDirection="row" position="relative">
          <ShiftDetails unassignedShift={unassignedShift} plannedBreaks={plannedBreaks} />
          <Box
            flexDirection="row"
            cursor="pointer"
            position="absolute"
            flexGrow
            style={{ right: '0', top: '0' }}
          >
            <UnassignedShiftMenu
              unassignedShift={unassignedShift}
              disabled={disabled}
              onAssignEmployeesAction={onAssignEmployeesAction}
              onEditAction={onEditAction}
              onSeeShiftAction={onSeeShiftAction}
              contractVersions={contractVersions}
            />
          </Box>
        </Box>

        <RolesDetails
          unassignedShift={unassignedShift}
          roles={roles}
          totalAssignedShifts={totalAssignedShifts}
          originalCounter={originalCounter}
        />

        {openAssignEmployeesModal && (
          <AssignEmployeesModal
            unassignedShift={unassignedShift}
            totalAssignedShifts={totalAssignedShifts}
            onClose={onCloseAssignShiftModal}
            contractVersions={contractVersions}
          />
        )}
        {openSeeModal && (
          <SeeUnassignedShiftModal
            unassignedShift={unassignedShift}
            date={unassignedShift.startAt}
            plannedBreaks={plannedBreaks}
            onClose={onCloseSeeShiftModal}
            onOpenEditModal={onOpenEditModal}
          />
        )}
        {openEditModal && (
          <EditUnassignedShiftModal
            unassignedShift={unassignedShift}
            totalAssignedShifts={totalAssignedShifts}
            onClose={onCloseEditShiftModal}
            contractVersions={contractVersions}
            roles={roles}
          />
        )}
      </Box>
    </>
  )
}

export default observer(UnassignedShiftItem)
